import Table from '@shared/components/Table';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadPersonFormReturnContext } from '@utils/personFormReturnStorage';
import { majorContractsService } from '@shared/services/majorContracts';
import InvestmentServicesForm from '@modules/staff/person/forms/personDocuments/components/InvestmentServicesForm';
import { sellerInvestmentService } from '@modules/staff/person/forms/personDocuments/services/sellerInvestment';
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig';
import DeleteModal from '@shared/components/DeleteModal';

export default function InvestmentServices() {
    const { t } = useTranslation();
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalState, setModalState] = useState({ visible: false, type: null });
    const navigate = useNavigate();
    const location = useLocation();
    const [person, setPerson] = useState(null);
    const [typeActivity, setTypeActivity] = useState(0);
    const [mainContracts, setMainContracts] = useState([]);
    const [rows, setRows] = useState([]);
    const columns = [
        { key: 'effectiveDate', label: t('common.fromDate') },
        { key: 'untilDate', label: t('common.toDate') }
    ];

    const data = rows;

    useEffect(() => {
        const navigationPerson = location.state?.person;
        const navigationTypeActivity = location.state?.typeActivity;
        if (navigationPerson) {
            setPerson(navigationPerson);
            if (navigationTypeActivity === 0 || navigationTypeActivity === 1) {
                setTypeActivity(navigationTypeActivity);
            }
            return;
        }
        const ctx = loadPersonFormReturnContext();
        if (ctx?.person) {
            setPerson(ctx.person);
            if (ctx.typeActivity === 0 || ctx.typeActivity === 1) {
                setTypeActivity(ctx.typeActivity);
            }
        }
    }, [location.state]);

    const employeeFullName = useMemo(() => {
        if (!person) return '';
        return (
            person.fullName ||
            [person.name, person.fathersName, person.surname].filter(Boolean).join(' ')
        );
    }, [person]);

    useEffect(() => {
        const id = person?.recordId ?? person?.empId ?? person?.id;
        if (!id) {
            setMainContracts([]);
            return;
        }

        majorContractsService
            .getMainContractsByPersonId(id)
            .then((data) => {
                const list = Array.isArray(data) ? data : (data?.content ?? []);
                setMainContracts(list);
            })
            .catch((err) => console.error('Error fetching main contracts:', err));
    }, [person?.recordId, person?.empId, person?.id]);

    const currentContractName = useMemo(() => {
        const c = mainContracts?.[0];
        if (!c) return '';
        return c.name || '';
    }, [mainContracts]);

    const currentContractId = useMemo(() => {
        const c = mainContracts?.[0];
        return c?.contractId ?? null;
    }, [mainContracts]);

    const personId = person?.personId ?? person?.id ?? null;
    const empId = person?.empId ?? '';

    const fetchSellerInvestments = useCallback(async () => {
        if (!empId && empId !== 0) {
            setRows([]);
            return;
        }
        if (typeActivity !== 0 && typeActivity !== 1) {
            setRows([]);
            return;
        }
        try {
            const response = await sellerInvestmentService.getByEmpIdAndTypeActivity(empId, typeActivity);
            const list = Array.isArray(response) ? response : (response?.content ?? []);
            setRows(list);
        } catch (e) {
            console.error('Error fetching seller investments:', e);
            setRows([]);
        }
    }, [empId, typeActivity]);

    useEffect(() => {
        fetchSellerInvestments();
    }, [fetchSellerInvestments]);

    const handleOpenModal = (type) => {
        if ((type === 'edit' || type === 'delete') && !selectedRecord) {
            notifyError(t('common.noSelection') || 'No selection');
            return;
        }
        setModalState({ visible: true, type });
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null });
    };

    const handleSubmitForm = async (payload) => {
        if (!personId || !empId || !currentContractId) {
            notifyError(t('common.errorMessage') || 'Missing required context');
            return;
        }

        try {
            if (modalState.type === 'edit') {
                const id = selectedRecord?.id;
                if (!id) {
                    notifyError(t('common.errorMessage') || 'Missing id');
                    return;
                }
                await sellerInvestmentService.update(id, payload);
                notifySuccess(t('common.updatedSuccessfully'));
            } else {
                await sellerInvestmentService.create(payload);
                notifySuccess(t('common.createdSuccessfully'));
            }
            await fetchSellerInvestments();
            handleCloseModal();
        } catch (e) {
            console.error('Error saving seller investment:', e);
            notifyError(e?.response?.data?.message || e?.response?.data || t('common.errorMessage'));
        }
    };

    const handleConfirmDelete = async () => {
        const id = selectedRecord?.id;
        if (!id) {
            notifyError(t('common.errorMessage') || 'Missing id');
            return;
        }
        try {
            await sellerInvestmentService.delete(id);
            notifySuccess(t('common.deletedSuccessfully'));
            setSelectedRecord(null);
            await fetchSellerInvestments();
            handleCloseModal();
        } catch (e) {
            console.error('Error deleting seller investment:', e);
            notifyError(e?.response?.data?.message || e?.response?.data || t('common.errorMessage'));
        }
    };

    const actionButtons = (
        <ActionButtons
            buttons={[{
                ...COMMON_BUTTONS.ADD,
                onClick: () => handleOpenModal('add')
            },
            {
                ...COMMON_BUTTONS.EDIT,
                onClick: () => handleOpenModal('edit')
            },
            {
                ...COMMON_BUTTONS.DELETE,
                onClick: () => handleOpenModal('delete')
            },
            ]}
            selectedRecord={selectedRecord}
        />
    );
    return (
        <div className="flex-column width-100">
            <Table
                title={typeActivity === 1 ? 'Служител на осигурителен посредник' : 'Продавач на инвестиционни услуги '}
                columns={columns}
                data={data}
                actionButtons={actionButtons}
                showPreviousPage={false}
                showCustomSearch={true}
                showPageSizeSelector={false}
                showPagination={false}
                employeeInfo={true}
                employeeEmpId={person?.empId || ''}
                employeeFullName={employeeFullName}
                employeeContractId={currentContractId ? String(currentContractId) : ''}
                employeeContractName={currentContractName}
                handleRowClick={(index) => {
                    const current = data || [];
                    if (index >= 0 && index < current.length) {
                        setSelectedRecord(current[index]);
                    }
                }}
            />
            <div className="flex-row justify-content-center mt-2">
                <button
                    type="button"
                    className="form-btn btn-red width-10"
                    onClick={() => navigate('/personnel/employment-contracts/register', {
                        state: { restorePersonForm: true },
                    })}
                >
                    {t('common.exit')}
                </button>
            </div >

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <InvestmentServicesForm
                    onClose={handleCloseModal}
                    onConfirm={handleSubmitForm}
                    editData={modalState.type === 'edit' ? selectedRecord : null}
                    personId={personId}
                    empId={empId}
                    contractId={currentContractId}
                    typeActivity={modalState.type === 'edit' ? (selectedRecord?.typeActivity ?? typeActivity) : typeActivity}
                />
            )}
            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    name={selectedRecord?.name || selectedRecord?.effectiveDate || selectedRecord?.id || ''}
                    entityType="record"
                />
            )}
        </div>

    );
}